import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { MdSearch } from "react-icons/md";
import { MapContainer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./App.css";
import GeohashGrid from "./components/GeohashGrid";
import AuthCallback from "./components/AuthCallback";
import Notification from "./components/Notification";
import { useNotification } from "./hooks/useNotification";
import siteConfig from "./siteConfig";
import WorldExploreBoundsFetcher from "./components/WorldExploreBoundsFetcher";
import LocationButton from "./components/LocationButton";
import WorldPhotoClusters from "./components/WorldPhotoClusters";
import ComingSoonBanner from "./components/ComingSoonBanner";
import OpenFreeMapLayer from "./components/OpenFreeMapLayer";

// Hedef/crosshair ikonu ile yanıp sönen marker (kullanıcı konumu için)
const customIcon = new L.DivIcon({
  className: "custom-user-marker",
  html: `
    <div style="
      width: 24px;
      height: 24px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: locationPulse 2s ease-in-out infinite, fadeOut 1s ease-out 3s forwards;
    ">
      <!-- Hedef/crosshair ikonu -->
      <svg 
        viewBox="0 0 40 40" 
        width="24" 
        height="24" 
        style="
          position: absolute;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
        "
      >
        <!-- Dış halka -->
        <circle 
          cx="20" 
          cy="20" 
          r="14" 
          fill="none" 
          stroke="#0f66f3" 
          stroke-width="1.5"
          opacity="0.8"
        />
        <!-- Ortadaki mavi daire -->
        <circle 
          cx="20" 
          cy="20" 
          r="6" 
          fill="#0f66f3"
        />
      </svg>
      
      <!-- Yanıp sönen halka efekti -->
      <div style="
        position: absolute;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 1.5px solid rgba(15, 102, 243, 0.4);
        animation: locationRipple 2s ease-out infinite;
      "></div>
    </div>
    <style>
      @keyframes locationPulse {
        0%, 100% {
          transform: scale(1);
          opacity: 1;
        }
        50% {
          transform: scale(1.1);
          opacity: 0.85;
        }
      }
      
      @keyframes locationRipple {
        0% {
          transform: scale(0.9);
          opacity: 0.8;
        }
        100% {
          transform: scale(1.8);
          opacity: 0;
        }
      }
      
      @keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
    </style>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Harita pozisyonunu güncelleyen bileşen
function MapController({ center, zoom }) {
  const map = useMap();

  // Sadece center ve zoom değiştiğinde haritayı güncelle
  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [map, center, zoom]);

  return null;
}

// Harita scroll/zoom optimizasyonu için bileşen
function MapScrollOptimizer() {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // Scroll wheel zoom hassasiyetini optimize et
    // Daha düşük değer = daha hassas zoom (daha az scroll ile zoom)
    if (map.scrollWheelZoom && map.scrollWheelZoom.options) {
      // wheelPxPerZoomLevel: Her zoom seviyesi için gereken piksel sayısı
      // Daha düşük değer = daha hassas (daha az scroll ile zoom)
      // Varsayılan 60, daha akıcı ve keskin zoom için 40'a düşürüyoruz
      map.scrollWheelZoom.options.wheelPxPerZoomLevel = 40;
    }

    // Zoom animasyon eşiğini ayarla (piksel cinsinden)
    // Daha küçük değer = daha hassas animasyon
    if (map.options) {
      map.options.zoomAnimationThreshold = 4;
      map.options.zoomAnimation = true;
      map.options.zoomAnimationDuration = 250; // ms cinsinden, daha hızlı animasyon
      map.options.markerZoomAnimation = true;
    }

    // Touch zoom için optimize et (mobil)
    if (map.touchZoom) {
      map.touchZoom.enable();
    }

    // Double click zoom'u optimize et
    if (map.doubleClickZoom) {
      map.doubleClickZoom.enable();
    }

    // Box zoom'u optimize et
    if (map.boxZoom) {
      map.boxZoom.enable();
    }

    return () => {
      // Cleanup gerekirse buraya eklenebilir
    };
  }, [map]);

  return null;
}

// Haritada konum araması sonrası geçici vurgu marker'ı
function LocationHighlight() {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    const handleHighlightLocation = (event) => {
      const { lat, lng } = event.detail || {};
      if (
        typeof lat !== "number" ||
        typeof lng !== "number" ||
        Number.isNaN(lat) ||
        Number.isNaN(lng)
      ) {
        return;
      }

      // Önceki marker varsa kaldır
      if (markerRef.current && map.hasLayer(markerRef.current)) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }

      const highlightIcon = new L.DivIcon({
        className: "custom-user-marker",
        html: `
          <div style="
            width: 24px;
            height: 24px;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: locationPulse 2s ease-in-out infinite, fadeOut 1s ease-out 3s forwards;
          ">
            <!-- Hedef/crosshair ikonu -->
            <svg 
              viewBox="0 0 40 40" 
              width="24" 
              height="24" 
              style="
                position: absolute;
                filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
              "
            >
              <!-- Dış halka -->
              <circle 
                cx="20" 
                cy="20" 
                r="14" 
                fill="none" 
                stroke="#0f66f3" 
                stroke-width="1.5"
                opacity="0.8"
              />
              <!-- Ortadaki mavi daire -->
              <circle 
                cx="20" 
                cy="20" 
                r="6" 
                fill="#0f66f3"
              />
            </svg>
            
            <!-- Yanıp sönen halka efekti -->
            <div style="
              position: absolute;
              width: 24px;
              height: 24px;
              border-radius: 50%;
              border: 1.5px solid rgba(15, 102, 243, 0.4);
              animation: locationRipple 2s ease-out infinite;
            "></div>
          </div>
          <style>
            @keyframes locationPulse {
              0%, 100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(1.1);
                opacity: 0.85;
              }
            }
            
            @keyframes locationRipple {
              0% {
                transform: scale(0.9);
                opacity: 0.8;
              }
              100% {
                transform: scale(1.8);
                opacity: 0;
              }
            }
            
            @keyframes fadeOut {
              0% {
                opacity: 1;
              }
              100% {
                opacity: 0;
              }
            }
          </style>
        `,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });

      const marker = L.marker([lat, lng], { icon: highlightIcon }).addTo(map);
      markerRef.current = marker;

      // 4 saniye sonra otomatik kaldır
      setTimeout(() => {
        if (markerRef.current && map.hasLayer(markerRef.current)) {
          map.removeLayer(markerRef.current);
          markerRef.current = null;
        }
      }, 4000);
    };

    window.addEventListener("highlightLocation", handleHighlightLocation);

    return () => {
      window.removeEventListener("highlightLocation", handleHighlightLocation);
      if (markerRef.current && map.hasLayer(markerRef.current)) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }
    };
  }, [map]);

  return null;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

function MainAppPage() {
  const { t } = useTranslation();
  const [mapCenter, setMapCenter] = useState([41.042, 29.01]); // İstanbul: hafif kuzey + batı (sol)
  const [mapZoom, setMapZoom] = useState(12);

  // Notification hook
  const { notification, showNotification, hideNotification } =
    useNotification();
  const [userLocation, setUserLocation] = useState(null);
  const [worldGeohashData, setWorldGeohashData] = useState(null);

  // Floating search bar state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const isNarrowView = useMediaQuery("(max-width: 768px)");
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);

  const handleWorldExploreData = useCallback((data) => {
    setWorldGeohashData((prev) => {
      if (!prev) return data;
      const hasNew = Object.keys(data).some((k) => !(k in prev));
      if (!hasNew) return prev;
      return { ...prev, ...data };
    });
  }, []);

  // Search: cleanup typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeout) clearTimeout(typingTimeout);
    };
  }, [typingTimeout]);

  // Search: click outside to close results
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setShowResults(false);
        setSearchPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchPanelOpen) return undefined;
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 80);
    return () => clearTimeout(id);
  }, [searchPanelOpen]);

  const searchLocation = async (query) => {
    if (!query || query.trim().length < 3) return;
    setIsSearching(true);
    try {
      const url = `${siteConfig.apiBaseUrl}/world/search/location?q=${encodeURIComponent(query)}`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      if (!response.ok) throw new Error(t("search.error"));
      const data = await response.json();
      setSearchResults(data || []);
    } catch (error) {
      console.error("Arama hatası:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 3) {
      setShowResults(true);
      if (typingTimeout) clearTimeout(typingTimeout);
      setTypingTimeout(setTimeout(() => searchLocation(value), 500));
    } else {
      setShowResults(false);
      setSearchResults([]);
    }
  };

  const handleSearchResultClick = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setSearchTerm(result.name || result.display_name.split(",")[0]);
    setShowResults(false);
    setSearchPanelOpen(false);
    handleLocationFound({ lat, lng, name: result.name || result.display_name });
    try {
      window.dispatchEvent(
        new CustomEvent("highlightLocation", { detail: { lat, lng } }),
      );
    } catch (e) {}
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) handleSearchResultClick(searchResults[0]);
  };

  // Yer bulunduğunda çağrılacak fonksiyon
  const handleLocationFound = (location) => {
    setMapCenter([location.lat, location.lng]);
    setUserLocation([location.lat, location.lng]); // Marker için konumu kaydet
    setMapZoom(16); // Yakınlaştırma seviyesini ayarla
  };

  return (
    <div className="App">
      <div className="map-wrapper">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          minZoom={3}
          maxZoom={17}
          zoomControl={false}
          scrollWheelZoom={true}
          style={{ height: "100vh", width: "100%" }}
          bounceAtZoomLimits={false}
          className="main-map-with-clusters"
        >
          <MapController center={mapCenter} zoom={mapZoom} />
          <MapScrollOptimizer />
          <LocationHighlight />
          <OpenFreeMapLayer />
          <WorldExploreBoundsFetcher onData={handleWorldExploreData} />
          <WorldPhotoClusters
            worldGeohashData={worldGeohashData}
            isRouteMode={false}
          />
          <GeohashGrid isRouteMode={false} worldGeohashData={worldGeohashData} />

          {/* Kullanıcı konumu marker'ı */}
          {userLocation && <Marker position={userLocation} icon={customIcon} />}

          {/* Konumuma git butonu */}
          <LocationButton
            onError={(message) => showNotification("error", message)}
            onLocationFound={handleLocationFound}
          />
        </MapContainer>
        <div
          className={`floating-search ${
            searchPanelOpen
              ? "floating-search--open"
              : "floating-search--collapsed"
          } ${isNarrowView ? "floating-search--narrow" : "floating-search--wide"}`}
          ref={searchContainerRef}
        >
          <div className="floating-search-morph">
            <form
              className="floating-search-morph-form"
              onSubmit={handleSearchSubmit}
            >
              <div className="floating-search-morph-inner">
                <MdSearch
                  className="floating-search-morph-icon"
                  size={22}
                  aria-hidden
                />
                {searchPanelOpen ? (
                  <>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("search.placeholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="floating-search-morph-input"
                      enterKeyHint="search"
                      aria-label={t("search.placeholder")}
                    />
                    {isSearching && (
                      <div className="floating-search-spinner" />
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className="floating-search-morph-hit"
                    onClick={() => setSearchPanelOpen(true)}
                    aria-label={t("search.placeholder")}
                  />
                )}
              </div>
            </form>
          </div>

          {searchPanelOpen && showResults && searchResults.length > 0 && (
            <div className="floating-search-results">
              {searchResults.map((result) => (
                <div
                  key={result.place_id}
                  className="floating-search-result-item"
                  onClick={() => handleSearchResultClick(result)}
                >
                  <svg
                    className="floating-result-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    aria-hidden
                  >
                    <path
                      fill="currentColor"
                      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-1.12 2.5-2.5 2.5z"
                    />
                  </svg>
                  <div className="floating-result-details">
                    <div className="floating-result-name">
                      {result.display_name.split(",")[0]}
                    </div>
                    <div className="floating-result-address">
                      {result.display_name.split(",").slice(1, 3).join(", ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {searchPanelOpen &&
            showResults &&
            isSearching &&
            searchResults.length === 0 && (
              <div className="floating-search-results">
                <div className="floating-search-status">
                  {t("search.searching")}
                </div>
              </div>
            )}

          {searchPanelOpen &&
            showResults &&
            !isSearching &&
            searchResults.length === 0 &&
            searchTerm.length > 2 && (
              <div className="floating-search-results">
                <div className="floating-search-status">
                  {t("search.noResults")}
                </div>
              </div>
            )}
        </div>
        <ComingSoonBanner />
      </div>

      {/* Notification Toast */}
      <Notification notification={notification} onClose={hideNotification} />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/" element={<MainAppPage />} />
        <Route path="/u/:username" element={<MainAppPage />} />
        <Route path="/r/:routeId" element={<MainAppPage />} />
      </Routes>
    </Router>
  );
}

export default App;
